import { useMemo, useState } from 'react';
import type { BrowserBulkActionMsg, BulkServerStatus, ServerDoc } from '@panel/shared';
import { useStore, dispatchBulk, cancelBulk, clearBulk, type BulkServerEntry } from '../lib/store.js';
import {
  IconBackup,
  IconForceStop,
  IconPlay,
  IconRestart,
  IconStop,
  IconUpdate,
  IconWarning,
  IconWipe,
} from '../components/Icons.js';

type BulkCommand = BrowserBulkActionMsg['command'];
type TargetKind = 'all' | 'group' | 'machine' | 'servers';

interface Me { discord_id: string; discord_username: string; role: 'admin' | 'operator' | 'viewer' }

interface CommandMeta {
  value: BulkCommand;
  label: string;
  desc: string;
  category: 'lifecycle' | 'maintenance';
  tone: 'neutral' | 'primary' | 'danger';
  icon: React.ComponentType<{ className?: string }>;
}

const COMMANDS: CommandMeta[] = [
  { value: 'start',      label: 'Start',       desc: 'Bring offline servers online',          category: 'lifecycle',   tone: 'primary', icon: IconPlay },
  { value: 'soft_stop',  label: 'Soft Stop',   desc: 'RCON quit + grace, then taskkill',      category: 'lifecycle',   tone: 'neutral', icon: IconStop },
  { value: 'force_stop', label: 'Force Stop',  desc: 'Immediate taskkill /F /T',              category: 'lifecycle',   tone: 'danger',  icon: IconForceStop },
  { value: 'restart',    label: 'Restart',     desc: 'Stop then start',                       category: 'lifecycle',   tone: 'neutral', icon: IconRestart },
  { value: 'update',     label: 'Update',      desc: 'SteamCMD validate + latest Oxide',      category: 'maintenance', tone: 'neutral', icon: IconUpdate },
  { value: 'wipe',       label: 'Wipe',        desc: 'Delete map+save (and BP if checked)',   category: 'maintenance', tone: 'danger',  icon: IconWipe },
  { value: 'backup',     label: 'Backup',      desc: 'Snapshot to R2',                        category: 'maintenance', tone: 'neutral', icon: IconBackup },
];

const UPDATE_STEPS = [
  { label: 'Manifest',  endPct: 10 },
  { label: 'SteamCMD',  endPct: 70 },
  { label: 'Oxide',     endPct: 94 },
  { label: 'Finalize',  endPct: 100 },
];

const STATUS_ORDER: BulkServerStatus[] = ['running', 'queued', 'error', 'done', 'skipped', 'cancelled'];

export default function Bulk({ me }: { me: Me }) {
  const servers = useStore((s) => s.servers);
  const liveStats = useStore((s) => s.liveStats);
  const nodeStatus = useStore((s) => s.nodeStatus);
  const bulk = useStore((s) => s.bulk);

  const [command, setCommand] = useState<BulkCommand>('update');
  const [targetKind, setTargetKind] = useState<TargetKind>('all');
  const [targetGroup, setTargetGroup] = useState<string>('');
  const [targetMachine, setTargetMachine] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [wipeBlueprints, setWipeBlueprints] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const allServers = useMemo(() => Array.from(servers.values()), [servers]);
  const groups = useMemo(() => Array.from(new Set(allServers.map((s) => s.Server_Group))).sort(), [allServers]);
  const machines = useMemo(() => Array.from(new Set(allServers.map((s) => s.Server_Machine))).sort(), [allServers]);

  if (targetGroup === '' && groups.length > 0) setTargetGroup(groups[0]);
  if (targetMachine === '' && machines.length > 0) setTargetMachine(machines[0]);

  const matchedServers = useMemo<ServerDoc[]>(() => {
    switch (targetKind) {
      case 'all':     return allServers;
      case 'group':   return allServers.filter((s) => s.Server_Group === targetGroup);
      case 'machine': return allServers.filter((s) => s.Server_Machine === targetMachine);
      case 'servers': return allServers.filter((s) => selectedIds.has(s.Server_ID));
    }
  }, [allServers, targetKind, targetGroup, targetMachine, selectedIds]);

  const offlineMachineCount = useMemo(() => {
    const targetMachines = new Set(matchedServers.map((s) => s.Server_Machine));
    let n = 0;
    for (const m of targetMachines) {
      if (!nodeStatus.get(m)?.online) n++;
    }
    return n;
  }, [matchedServers, nodeStatus]);

  const cmdMeta = COMMANDS.find((c) => c.value === command)!;
  const canRun = me.role !== 'viewer' && matchedServers.length > 0 && (!bulk || bulk.done);
  const formDisabled = !!bulk && !bulk.done;

  const buildTarget = (): BrowserBulkActionMsg['target'] => {
    switch (targetKind) {
      case 'all':     return { kind: 'all' };
      case 'group':   return { kind: 'group', group: targetGroup };
      case 'machine': return { kind: 'machine', machine: targetMachine };
      case 'servers': return { kind: 'servers', ids: Array.from(selectedIds) };
    }
  };

  const onConfirm = () => {
    const args: Record<string, unknown> = {};
    if (command === 'wipe') args.blueprints = wipeBlueprints;
    dispatchBulk(command, buildTarget(), args);
    setConfirmOpen(false);
  };

  const CmdIcon = cmdMeta.icon;
  const machineSet = new Set(matchedServers.map((s) => s.Server_Machine));

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Bulk Actions</h1>
        <p className="text-sm text-fg-muted mt-1 max-w-3xl">
          Run a single command against many servers at once. Per-machine queue is sequential
          (so SteamCMD can't trip over its own depot locks); machines run in parallel.
        </p>
      </header>

      {/* CONFIGURE */}
      <section className="card overflow-hidden">
        <div className="px-5 h-12 border-b border-bg-border bg-bg-raised/30 flex items-center gap-2">
          <StepBadge n={1} />
          <h3 className="font-semibold text-sm text-fg">Choose a command</h3>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <div className="label">Lifecycle</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {COMMANDS.filter((c) => c.category === 'lifecycle').map((c) => (
                <CommandTile
                  key={c.value}
                  meta={c}
                  active={command === c.value}
                  disabled={formDisabled}
                  onClick={() => setCommand(c.value)}
                />
              ))}
            </div>
          </div>
          <div>
            <div className="label">Maintenance</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {COMMANDS.filter((c) => c.category === 'maintenance').map((c) => (
                <CommandTile
                  key={c.value}
                  meta={c}
                  active={command === c.value}
                  disabled={formDisabled}
                  onClick={() => setCommand(c.value)}
                />
              ))}
            </div>
          </div>

          {command === 'wipe' && (
            <label className="flex items-center gap-2 text-sm pt-2 border-t border-bg-border cursor-pointer">
              <input
                type="checkbox"
                checked={wipeBlueprints}
                onChange={(e) => setWipeBlueprints(e.target.checked)}
                className="accent-accent w-4 h-4 cursor-pointer"
              />
              <span className="text-fg-muted">Also wipe blueprints (full BP wipe)</span>
            </label>
          )}
        </div>
      </section>

      {/* TARGET */}
      <section className="card overflow-hidden">
        <div className="px-5 h-12 border-b border-bg-border bg-bg-raised/30 flex items-center gap-2">
          <StepBadge n={2} />
          <h3 className="font-semibold text-sm text-fg">Pick targets</h3>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {(['all', 'group', 'machine', 'servers'] as const).map((k) => (
              <button
                key={k}
                type="button"
                disabled={formDisabled}
                onClick={() => setTargetKind(k)}
                className={`px-3 h-10 text-sm rounded-lg border cursor-pointer transition-colors disabled:opacity-50 capitalize ${
                  targetKind === k ? 'bg-accent/10 border-accent/60 text-accent font-medium' : 'bg-bg-raised border-bg-border text-fg-muted hover:text-fg hover:border-bg-ring'
                }`}
              >
                {k === 'servers' ? 'Specific servers' : k === 'all' ? 'All servers' : `By ${k}`}
              </button>
            ))}
          </div>

          {targetKind === 'group' && (
            <select
              className="input w-full md:w-64"
              value={targetGroup}
              onChange={(e) => setTargetGroup(e.target.value)}
              disabled={formDisabled}
            >
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          )}

          {targetKind === 'machine' && (
            <select
              className="input w-full md:w-64"
              value={targetMachine}
              onChange={(e) => setTargetMachine(e.target.value)}
              disabled={formDisabled}
            >
              {machines.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          )}

          {targetKind === 'servers' && (
            <div className="max-h-64 overflow-y-auto border border-bg-border rounded-lg p-1 bg-bg-raised/30">
              {allServers.length === 0 ? (
                <div className="text-fg-dim text-sm italic p-3">No servers yet.</div>
              ) : allServers.map((s) => {
                const checked = selectedIds.has(s.Server_ID);
                const live = liveStats.get(s.Server_ID);
                const machineOnline = !!nodeStatus.get(s.Server_Machine)?.online;
                const dotClass = !machineOnline
                  ? 'bg-status-offline'
                  : live?.status === 'online' ? 'bg-status-online'
                    : live?.status === 'restarting' ? 'bg-status-updating'
                    : 'bg-status-offline';
                return (
                  <label
                    key={s.Server_ID}
                    className={`flex items-center gap-2 text-sm cursor-pointer hover:bg-bg-hover rounded-md px-2 py-1.5 ${machineOnline ? '' : 'opacity-60'}`}
                    title={machineOnline ? undefined : 'Machine unreachable — this server will be skipped on run'}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setSelectedIds((cur) => {
                          const next = new Set(cur);
                          if (next.has(s.Server_ID)) next.delete(s.Server_ID); else next.add(s.Server_ID);
                          return next;
                        });
                      }}
                      className="accent-accent w-4 h-4 cursor-pointer"
                    />
                    <span className={`dot ${dotClass}`} />
                    <span className="font-medium text-fg truncate">{s.Server_Name}</span>
                    <span className="text-fg-dim text-xs ml-auto truncate">
                      {s.Server_Machine}
                      {!machineOnline && <span className="text-status-error ml-1">(offline)</span>}
                      <span className="ml-1">· {s.Server_Group}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* PREVIEW + RUN */}
      <section className="card p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <CmdIcon />
            <span className="font-medium text-fg">{cmdMeta.label}</span>
            {command === 'wipe' && (
              <span className="text-xs text-fg-dim">· {wipeBlueprints ? 'map + BP' : 'map only'}</span>
            )}
          </div>
          <span className="text-fg-dim">→</span>
          <div className="flex items-center gap-3 tabular-nums flex-wrap">
            <span><strong className="text-fg">{matchedServers.length}</strong> <span className="text-fg-dim">server{matchedServers.length === 1 ? '' : 's'}</span></span>
            <span><strong className="text-fg">{machineSet.size}</strong> <span className="text-fg-dim">machine{machineSet.size === 1 ? '' : 's'}</span></span>
            {offlineMachineCount > 0 && (
              <span className="text-status-warn flex items-center gap-1">
                <IconWarning className="w-3.5 h-3.5" />
                {offlineMachineCount} offline (skipped)
              </span>
            )}
          </div>
        </div>

        <button
          className={cmdMeta.tone === 'danger' ? 'btn-danger' : 'btn-primary'}
          disabled={!canRun}
          onClick={() => setConfirmOpen(true)}
        >
          <CmdIcon />
          Run {cmdMeta.label}
        </button>
      </section>

      {bulk && <BulkStatus />}

      {confirmOpen && (
        <ConfirmModal
          command={command}
          target={buildTarget()}
          matchedCount={matchedServers.length}
          tone={cmdMeta.tone}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={onConfirm}
        />
      )}
    </div>
  );
}

function StepBadge({ n }: { n: number }) {
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent/15 text-accent text-[10px] font-semibold ring-1 ring-accent/25">
      {n}
    </span>
  );
}

function CommandTile({
  meta, active, disabled, onClick,
}: {
  meta: CommandMeta;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = meta.icon;
  const toneActive =
    meta.tone === 'danger' ? 'border-status-error/50 bg-status-error/8'
      : meta.tone === 'primary' ? 'border-status-online/50 bg-status-online/8'
      : 'border-accent/50 bg-accent/8';
  const iconTone =
    meta.tone === 'danger' ? 'text-status-error'
      : meta.tone === 'primary' ? 'text-status-online'
      : 'text-accent';

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`text-left p-3 rounded-lg border cursor-pointer transition-all duration-150 disabled:opacity-50 flex gap-3 items-start ${
        active ? `${toneActive} text-fg` : 'bg-bg-raised border-bg-border text-fg-muted hover:text-fg hover:border-bg-ring'
      }`}
    >
      <span className={`mt-0.5 shrink-0 ${active ? iconTone : 'text-fg-dim'}`}>
        <Icon />
      </span>
      <span className="min-w-0">
        <div className="text-sm font-medium leading-tight text-fg">{meta.label}</div>
        <div className="text-[11px] text-fg-dim leading-snug mt-0.5">{meta.desc}</div>
      </span>
    </button>
  );
}

function BulkStatus() {
  const bulk = useStore((s) => s.bulk);
  if (!bulk) return null;

  const list = Array.from(bulk.servers.values()).sort((a, b) =>
    STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)
    || a.Server_Name.localeCompare(b.Server_Name),
  );

  const counts: Record<BulkServerStatus, number> = {
    queued: 0, running: 0, done: 0, error: 0, skipped: 0, cancelled: 0,
  };
  for (const e of list) counts[e.status]++;

  const errors = list.filter((e) => e.status === 'error');
  const cmdMeta = COMMANDS.find((c) => c.value === bulk.command);
  const CmdIcon = cmdMeta?.icon;

  return (
    <section className="card overflow-hidden">
      <header className="px-5 h-14 border-b border-bg-border flex items-center justify-between gap-3 bg-bg-raised/30">
        <div className="flex items-center gap-3">
          {CmdIcon && <CmdIcon />}
          <div>
            <h3 className="font-semibold text-sm text-fg">
              {bulk.done ? `${cmdMeta?.label ?? bulk.command} complete` : `${cmdMeta?.label ?? bulk.command} in progress…`}
            </h3>
            <div className="text-xs text-fg-dim mt-0.5">
              {bulk.servers.size} server{bulk.servers.size === 1 ? '' : 's'} · started {fmtRelative(bulk.startedAt)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!bulk.done && (
            <button className="btn-ghost btn-sm" onClick={() => cancelBulk(bulk.bulk_id)}>
              Cancel queue
            </button>
          )}
          {bulk.done && (
            <button className="btn-ghost btn-sm" onClick={clearBulk}>Dismiss</button>
          )}
        </div>
      </header>

      <div className="px-5 py-3 border-b border-bg-border flex items-center gap-2 flex-wrap text-xs">
        <Chip label="Running"   value={counts.running}   color="bg-status-updating/15 text-status-updating ring-status-updating/30" />
        <Chip label="Queued"    value={counts.queued}    color="bg-bg-raised text-fg-muted ring-bg-border" />
        <Chip label="Done"      value={counts.done}      color="bg-status-online/15 text-status-online ring-status-online/30" />
        {counts.error > 0 &&     <Chip label="Failed"    value={counts.error}     color="bg-status-error/15 text-status-error ring-status-error/30" />}
        {counts.skipped > 0 &&   <Chip label="Skipped"   value={counts.skipped}   color="bg-status-warn/15 text-status-warn ring-status-warn/30" />}
        {counts.cancelled > 0 && <Chip label="Cancelled" value={counts.cancelled} color="bg-bg-card text-fg-dim ring-bg-border" />}
      </div>

      <div className="px-3 py-3 space-y-1">
        {list.map((entry) => (
          <BulkRow key={entry.Server_ID} entry={entry} command={bulk.command} />
        ))}
      </div>

      {errors.length > 0 && (
        <div className="border-t border-bg-border px-5 py-4 bg-status-error/5">
          <div className="text-status-error font-medium text-sm mb-2 flex items-center gap-2">
            <IconWarning className="w-3.5 h-3.5" />
            {errors.length} error{errors.length === 1 ? '' : 's'}
          </div>
          <ul className="text-xs space-y-1">
            {errors.map((e) => (
              <li key={e.Server_ID} className="text-fg-muted">
                <span className="text-fg font-medium">{e.Server_Name}</span>:{' '}
                <span className="text-status-error">{e.error ?? 'unknown error'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Chip({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <span className={`px-2.5 py-1 rounded-full font-medium tabular-nums ring-1 ${color}`}>
      {value} {label}
    </span>
  );
}

const STATUS_PILL: Record<BulkServerStatus, string> = {
  queued:    'bg-bg-card text-fg-dim ring-bg-border',
  running:   'bg-status-updating/12 text-status-updating ring-status-updating/30',
  done:      'bg-status-online/12 text-status-online ring-status-online/30',
  error:     'bg-status-error/12 text-status-error ring-status-error/30',
  skipped:   'bg-status-warn/12 text-status-warn ring-status-warn/30',
  cancelled: 'bg-bg-card text-fg-dim ring-bg-border',
};

function BulkRow({ entry, command }: { entry: BulkServerEntry; command: BulkCommand }) {
  const showSteps = command === 'update' && entry.status !== 'queued' && entry.status !== 'cancelled' && entry.status !== 'skipped';

  return (
    <div className={`px-3 py-2.5 rounded-lg border bg-bg-raised border-bg-border transition-colors ${
      entry.status === 'running' ? 'ring-1 ring-status-updating/30 border-status-updating/30' : ''
    }`}>
      <div className="flex items-center gap-3">
        <span className={`px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wide font-semibold w-20 text-center ring-1 ${STATUS_PILL[entry.status]}`}>
          {entry.status}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate text-fg">{entry.Server_Name}</div>
          <div className="text-[11px] text-fg-dim truncate">
            {entry.Server_Machine}
            {entry.message && <> · {entry.message}</>}
          </div>
        </div>
        {entry.status === 'running' && !showSteps && (
          <>
            <div className="w-24 h-1 bg-bg-border rounded-full overflow-hidden">
              <div className="h-full bg-status-updating transition-all duration-300" style={{ width: `${entry.progress}%` }} />
            </div>
            <span className="text-[10px] text-fg-dim tabular-nums w-8 text-right">{entry.progress}%</span>
          </>
        )}
      </div>

      {showSteps && (
        <div className="mt-2 ml-[88px]">
          <UpdateStepper progress={entry.progress} status={entry.status} />
        </div>
      )}
    </div>
  );
}

function UpdateStepper({ progress, status }: { progress: number; status: BulkServerStatus }) {
  return (
    <div className="flex items-start">
      {UPDATE_STEPS.map((step, idx) => {
        const prevEnd = idx === 0 ? 0 : UPDATE_STEPS[idx - 1].endPct;
        let state: 'done' | 'active' | 'pending' | 'failed';
        if (status === 'error' && progress >= prevEnd && progress < step.endPct) state = 'failed';
        else if (status === 'done' || progress >= step.endPct) state = 'done';
        else if (progress >= prevEnd) state = 'active';
        else state = 'pending';

        const isLast = idx === UPDATE_STEPS.length - 1;
        return (
          <div key={step.label} className="flex-1 flex flex-col items-center">
            <div className="flex items-center w-full">
              <div className={`flex-1 h-px ${idx === 0 ? 'invisible' : state === 'done' ? 'bg-status-online' : state === 'failed' ? 'bg-status-error' : 'bg-bg-border'}`} />
              <StepDot state={state} />
              <div className={`flex-1 h-px ${isLast ? 'invisible' : state === 'done' ? 'bg-status-online' : 'bg-bg-border'}`} />
            </div>
            <div className={`text-[9px] mt-1 ${
              state === 'done' ? 'text-fg-muted'
                : state === 'active' ? 'text-fg'
                : state === 'failed' ? 'text-status-error'
                : 'text-fg-dim'
            }`}>
              {step.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StepDot({ state }: { state: 'done' | 'active' | 'pending' | 'failed' }) {
  const base = 'w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors';
  if (state === 'done') {
    return (
      <div className={`${base} bg-status-online border-status-online`}>
        <svg width={8} height={8} viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M2.5 6.5l2.5 2.5 4.5-5" />
        </svg>
      </div>
    );
  }
  if (state === 'active') {
    return (
      <div className={`${base} bg-bg border-status-updating`}>
        <span className="w-1 h-1 rounded-full bg-status-updating animate-pulse" />
      </div>
    );
  }
  if (state === 'failed') {
    return (
      <div className={`${base} bg-status-error/10 border-status-error`}>
        <svg width={8} height={8} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="text-status-error" aria-hidden>
          <path d="M3 3l6 6M9 3l-6 6" />
        </svg>
      </div>
    );
  }
  return <div className={`${base} bg-bg-raised border-bg-border`} />;
}

function ConfirmModal({
  command, target, matchedCount, tone, onCancel, onConfirm,
}: {
  command: BulkCommand;
  target: BrowserBulkActionMsg['target'];
  matchedCount: number;
  tone: CommandMeta['tone'];
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const targetDesc =
    target.kind === 'all' ? 'every server'
      : target.kind === 'group' ? `every server in group ${target.group}`
      : target.kind === 'machine' ? `every server on machine ${target.machine}`
      : `${target.ids.length} selected server${target.ids.length === 1 ? '' : 's'}`;

  return (
    <div className="fixed inset-0 z-50 bg-bg/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onCancel}>
      <div className="card p-6 w-full max-w-md shadow-modal animate-slide-up" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="text-lg font-semibold mb-2">Confirm bulk action</h3>
        <p className="text-sm text-fg-muted mb-4">
          Run <span className="text-fg font-mono">{command}</span> against{' '}
          <span className="text-fg">{targetDesc}</span>{' '}
          (<span className="text-fg">{matchedCount} server{matchedCount === 1 ? '' : 's'}</span>)?
        </p>
        <p className="text-xs text-fg-dim mb-5">
          Servers on each machine run sequentially. Different machines run in parallel.
        </p>
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onCancel}>Cancel</button>
          <button
            className={tone === 'danger' ? 'btn-danger' : 'btn-primary'}
            onClick={onConfirm}
            autoFocus
          >
            Run
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtRelative(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}
